import type { GeoreferencedMap } from '~/api/georeferencedMaps';
import type { TOCParent } from '~/common/types';

export const GEOREFERENCED_MAP_LAYER_PREFIX = 'georef-';

/** Panel layer id for a page under one agency, so a page listed under two agencies toggles independently. */
export function georeferencedMapLayerId(map: Pick<GeoreferencedMap, 'id' | 'agencyId'>): string {
  return `${GEOREFERENCED_MAP_LAYER_PREFIX}${map.id}:${map.agencyId}`;
}

/** Mapbox source and layer id for a page, shared by every agency it is listed under so it is drawn once. */
export function georeferencedMapRenderId(mapId: string): string {
  return `${GEOREFERENCED_MAP_LAYER_PREFIX}${mapId}`;
}

/** Area of the footprint's bounding box in square degrees; only used to compare maps. */
export function footprintArea(map: GeoreferencedMap): number {
  const ring = map.footprint.coordinates[0];
  const lngs = ring.map(([lng]) => lng);
  const lats = ring.map(([, lat]) => lat);
  return (Math.max(...lngs) - Math.min(...lngs)) * (Math.max(...lats) - Math.min(...lats));
}

/** Visible pages in draw order, once each: largest footprint first, so detail pages land on top of overview pages. */
export function visibleMapsInDrawOrder(
  maps: readonly GeoreferencedMap[] | undefined,
  visibleLayerIds: ReadonlySet<string>,
): GeoreferencedMap[] {
  const visible = new Map<string, GeoreferencedMap>();
  for (const map of maps ?? []) {
    if (!visible.has(map.id) && visibleLayerIds.has(georeferencedMapLayerId(map))) visible.set(map.id, map);
  }
  return [...visible.values()].sort((a, b) => footprintArea(b) - footprintArea(a));
}

/** Geo-referenced Maps panel tree for the project's maps: agency → source file → page, each page leaf keyed by its layer id. */
export function georeferencedMapsToTocItems(maps: readonly GeoreferencedMap[] | undefined): TOCParent[] {
  const agencies = new Map<string, TOCParent>();
  const files = new Map<string, TOCParent>();
  const sorted = [...(maps ?? [])].sort(
    (a, b) =>
      a.agencyName.localeCompare(b.agencyName) ||
      a.sourceFileName.localeCompare(b.sourceFileName) ||
      a.pageNumber - b.pageNumber,
  );
  for (const map of sorted) {
    let agency = agencies.get(map.agencyId);
    if (!agency) {
      agency = {
        label: map.agencyName,
        sectionId: `${GEOREFERENCED_MAP_LAYER_PREFIX}agency:${map.agencyId}`,
        expanded: true,
        children: [],
      };
      agencies.set(map.agencyId, agency);
    }
    // A source file linked to several agencies' requests appears under each of them.
    const fileKey = `${map.agencyId}:${map.sourceFileId}`;
    let file = files.get(fileKey);
    if (!file) {
      file = {
        label: map.sourceFileName,
        ...(map.utilityType && { labelSuffix: map.utilityType }),
        sectionId: `${GEOREFERENCED_MAP_LAYER_PREFIX}file:${fileKey}`,
        expanded: false,
        children: [],
      };
      files.set(fileKey, file);
      agency.children.push(file);
    }
    // `pageNumber` is 0-based in Grid Vision's manifest.
    file.children.push({ id: georeferencedMapLayerId(map), label: `Page ${map.pageNumber + 1}` });
  }
  return [...agencies.values()];
}
